import React, { useState } from "react";
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Paper,
  Grid,
  MenuItem,
  Stepper,
  Step,
  StepLabel,
  useMediaQuery,
  useTheme,
  ToggleButtonGroup,
  ToggleButton,
} from "@mui/material";
import { Send, Logout } from "@mui/icons-material";
import { submitLoanApplication } from "../../services/api";
import { validateLoanApplication } from "../../services/validation";
import FileUpload from "./FileUpload";
import LoadingSpinner from "../common/LoadingSpinner";
import Alert from "../common/Alert";

const steps = [
  "Personal Information",
  "Loan Details",
  "Employment",
  "Banking & Documents",
];

const LoanApplicationForm = ({ onLogout }) => {
  const [activeStep, setActiveStep] = useState(0);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [formData, setFormData] = useState({
    personalInfo: {
      fullName: "",
      email: "",
      phone: "",
      idNumber: "",
    },
    loanDetails: {
      amount: "",
      period: "",
    },
    employmentInfo: {
      employmentStatus: "employed",
      employer: "",
      position: "",
      contact: "",
      monthlyIncome: "",
      duration: "",
      incomeSource: "",
      grantType: "",
    },
    bankingDetails: {
      bankName: "",
      accountType: "",
      accountNumber: "",
      branchCode: "",
    },
  });
  const [files, setFiles] = useState([]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState({
    open: false,
    severity: "info",
    message: "",
  });

  const handleChange = (section, field) => (e) => {
    const value = e.target.value;
    setFormData((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value,
      },
    }));
    // Clear error
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  const handleEmploymentStatusChange = (event, newStatus) => {
    if (newStatus !== null) {
      setFormData((prev) => ({
        ...prev,
        employmentInfo: {
          ...prev.employmentInfo,
          employmentStatus: newStatus,
          ...(newStatus !== "employed" && {
            employer: "",
            position: "",
            contact: "",
            duration: "",
          }),
          ...(newStatus !== "unemployed" &&
            newStatus !== "sassa" && {
              incomeSource: "",
              grantType: "",
            }),
        },
      }));
    }
  };

  const handleNext = () => {
    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate form
    const validation = validateLoanApplication(formData);
    if (!validation.isValid) {
      setErrors(validation.errors);
      setAlert({
        open: true,
        severity: "error",
        message: "Please fix the errors in the form",
      });
      return;
    }

    setLoading(true);

    try {
      // Create FormData for file upload
      const submitData = new FormData();
      submitData.append("personalInfo", JSON.stringify(formData.personalInfo));
      submitData.append("loanDetails", JSON.stringify(formData.loanDetails));
      submitData.append(
        "employmentInfo",
        JSON.stringify(formData.employmentInfo)
      );
      submitData.append(
        "bankingDetails",
        JSON.stringify(formData.bankingDetails)
      );

      // Append files
      files.forEach((file) => {
        submitData.append("documents", file);
      });

      const result = await submitLoanApplication(submitData);
      setAlert({
        open: true,
        severity: "success",
        message: result.message || "Application submitted successfully!",
      });

      // Reset form
      setFormData({
        personalInfo: { fullName: "", email: "", phone: "", idNumber: "" },
        loanDetails: { amount: "", period: "" },
        employmentInfo: {
          employmentStatus: "employed",
          employer: "",
          position: "",
          contact: "",
          monthlyIncome: "",
          duration: "",
          incomeSource: "",
          grantType: "",
        },
        bankingDetails: {
          bankName: "",
          accountType: "",
          accountNumber: "",
          branchCode: "",
        },
      });
      setFiles([]);
      setActiveStep(0);
    } catch (error) {
      setAlert({
        open: true,
        severity: "error",
        message: error.message || "Failed to submit application",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCloseAlert = () => {
    setAlert((prev) => ({ ...prev, open: false }));
  };

  const handleLogout = () => {
    localStorage.removeItem("loanAppAuth");
    localStorage.removeItem("loanAppAuthTime");
    onLogout();
  };

  if (loading) {
    return <LoadingSpinner message="Submitting your application..." />;
  }

  return (
    <Box
      sx={{
        py: { xs: 4, md: 6 },
        bgcolor: "background.default",
        minHeight: "80vh",
      }}
    >
      <Container maxWidth="md">
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 4,
          }}
        >
          <Typography
            variant="h4"
            sx={{ fontWeight: 700, color: "primary.main" }}
          >
            Loan Application
          </Typography>
          <Button
            startIcon={<Logout />}
            onClick={handleLogout}
            variant="outlined"
            size="small"
          >
            Logout
          </Button>
        </Box>

        <Paper sx={{ p: { xs: 2, md: 4 } }}>
          <Stepper
            activeStep={activeStep}
            sx={{
              mb: 4,
              "& .MuiStepLabel-label": {
                fontSize: { xs: "0.95rem", sm: "1rem" },
              },
              "& .MuiStep-root": {
                mb: { xs: -1, sm: -1 },
              },
            }}
            orientation={isMobile ? "vertical" : "horizontal"}
            alternativeLabel={!isMobile}
          >
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          <form onSubmit={handleSubmit}>
            {/* Step 1: Personal Information */}
            {activeStep === 0 && (
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>
                    Personal Information
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Full Name"
                    value={formData.personalInfo.fullName}
                    onChange={handleChange("personalInfo", "fullName")}
                    error={!!errors.fullName}
                    helperText={errors.fullName}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Email Address"
                    type="email"
                    value={formData.personalInfo.email}
                    onChange={handleChange("personalInfo", "email")}
                    error={!!errors.email}
                    helperText={errors.email}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Phone Number"
                    value={formData.personalInfo.phone}
                    onChange={handleChange("personalInfo", "phone")}
                    error={!!errors.phone}
                    helperText={errors.phone || "10-digit number"}
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="ID Number"
                    value={formData.personalInfo.idNumber}
                    onChange={handleChange("personalInfo", "idNumber")}
                    error={!!errors.idNumber}
                    helperText={
                      errors.idNumber || "South African ID number (13 digits)"
                    }
                    required
                  />
                </Grid>
              </Grid>
            )}

            {/* Step 2: Loan Details */}
            {activeStep === 1 && (
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>
                    Loan Details
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Loan Amount (R)"
                    type="number"
                    value={formData.loanDetails.amount}
                    onChange={handleChange("loanDetails", "amount")}
                    error={!!errors.amount}
                    helperText={errors.amount || "R100 - R3,000"}
                    InputProps={{ inputProps: { min: 100, max: 3000 } }}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Repayment Period"
                    select
                    value={formData.loanDetails.period}
                    onChange={handleChange("loanDetails", "period")}
                    error={!!errors.period}
                    helperText={errors.period || "Select loan term"}
                    required
                  >
                    {Array.from({ length: 30 }, (_, i) => i + 1).map((day) => (
                      <MenuItem key={day} value={day}>
                        {day} {day === 1 ? "Day" : "Days"}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              </Grid>
            )}

            {/* Step 3: Employment Information */}
            {activeStep === 2 && (
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>
                    Employment Information
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <Typography
                    variant="body2"
                    sx={{ mb: 1, color: "text.secondary" }}
                  >
                    Employment Status
                  </Typography>
                  <ToggleButtonGroup
                    value={formData.employmentInfo.employmentStatus}
                    exclusive
                    onChange={handleEmploymentStatusChange}
                    fullWidth
                    sx={{
                      display: "flex",
                      "& .MuiToggleButton-root": {
                        flex: 1,
                        py: 1.5,
                      },
                    }}
                  >
                    <ToggleButton value="employed">Employed</ToggleButton>
                    <ToggleButton value="unemployed">Unemployed</ToggleButton>
                    <ToggleButton value="sassa">SASSA Grant</ToggleButton>
                  </ToggleButtonGroup>
                </Grid>

                {formData.employmentInfo.employmentStatus === "employed" && (
                  <>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Employer Name"
                        value={formData.employmentInfo.employer}
                        onChange={handleChange("employmentInfo", "employer")}
                        error={!!errors.employer}
                        helperText={errors.employer}
                        required
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Position/Job Title"
                        value={formData.employmentInfo.position}
                        onChange={handleChange("employmentInfo", "position")}
                        error={!!errors.position}
                        helperText={errors.position}
                        required
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Employer Contact Number"
                        value={formData.employmentInfo.contact}
                        onChange={handleChange("employmentInfo", "contact")}
                        error={!!errors.contact}
                        helperText={errors.contact}
                        required
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Employment Duration"
                        value={formData.employmentInfo.duration}
                        onChange={handleChange("employmentInfo", "duration")}
                        error={!!errors.duration}
                        helperText={errors.duration || "e.g., 2 years"}
                        required
                      />
                    </Grid>
                  </>
                )}

                {formData.employmentInfo.employmentStatus === "unemployed" && (
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Source of Income"
                      select
                      value={formData.employmentInfo.incomeSource}
                      onChange={handleChange("employmentInfo", "incomeSource")}
                      error={!!errors.incomeSource}
                      helperText={
                        errors.incomeSource || "Select your income source"
                      }
                      required
                    >
                      <MenuItem value="unemployment_benefits">
                        Unemployment Benefits
                      </MenuItem>
                      <MenuItem value="family_support">Family Support</MenuItem>
                      <MenuItem value="savings">Savings</MenuItem>
                      <MenuItem value="freelance">Freelance/Gig Work</MenuItem>
                      <MenuItem value="other">Other</MenuItem>
                    </TextField>
                  </Grid>
                )}

                {formData.employmentInfo.employmentStatus === "sassa" && (
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Grant Type"
                      select
                      value={formData.employmentInfo.grantType}
                      onChange={handleChange("employmentInfo", "grantType")}
                      error={!!errors.grantType}
                      helperText={
                        errors.grantType || "Select your SASSA grant type"
                      }
                      required
                    >
                      <MenuItem value="old_age">Old Age Pension</MenuItem>
                      <MenuItem value="disability">Disability Grant</MenuItem>
                      <MenuItem value="child_support">
                        Child Support Grant
                      </MenuItem>
                      <MenuItem value="foster_care">Foster Care Grant</MenuItem>
                      <MenuItem value="care_dependency">
                        Care Dependency Grant
                      </MenuItem>
                      <MenuItem value="social_relief">
                        Social Relief of Distress
                      </MenuItem>
                    </TextField>
                  </Grid>
                )}

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Monthly Income (R)"
                    type="number"
                    value={formData.employmentInfo.monthlyIncome}
                    onChange={handleChange("employmentInfo", "monthlyIncome")}
                    error={!!errors.monthlyIncome}
                    helperText={errors.monthlyIncome}
                    required
                  />
                </Grid>
              </Grid>
            )}

            {/* Step 4: Banking Details & Documents */}
            {activeStep === 3 && (
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>
                    Banking Details
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Bank Name"
                    select
                    value={formData.bankingDetails.bankName}
                    onChange={handleChange("bankingDetails", "bankName")}
                    error={!!errors.bankName}
                    helperText={errors.bankName}
                    required
                  >
                    {[
                      "ABSA",
                      "FNB",
                      "Standard Bank",
                      "Nedbank",
                      "Capitec",
                      "Other",
                    ].map((bank) => (
                      <MenuItem key={bank} value={bank}>
                        {bank}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Account Type"
                    select
                    value={formData.bankingDetails.accountType}
                    onChange={handleChange("bankingDetails", "accountType")}
                    error={!!errors.accountType}
                    helperText={errors.accountType}
                    required
                  >
                    {["Savings", "Cheque", "Current"].map((type) => (
                      <MenuItem key={type} value={type}>
                        {type}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Account Number"
                    value={formData.bankingDetails.accountNumber}
                    onChange={handleChange("bankingDetails", "accountNumber")}
                    error={!!errors.accountNumber}
                    helperText={errors.accountNumber}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Branch Code"
                    value={formData.bankingDetails.branchCode}
                    onChange={handleChange("bankingDetails", "branchCode")}
                    error={!!errors.branchCode}
                    helperText={errors.branchCode || "6 digits"}
                    required
                  />
                </Grid>
                <Grid item xs={12} sx={{ mt: 3 }}>
                  <FileUpload files={files} onFilesChange={setFiles} />
                </Grid>
              </Grid>
            )}

            {/* Navigation Buttons */}
            <Box
              sx={{ display: "flex", justifyContent: "space-between", mt: 4 }}
            >
              <Button
                disabled={activeStep === 0}
                onClick={handleBack}
                variant="outlined"
              >
                Back
              </Button>
              {activeStep < steps.length - 1 ? (
                <Button onClick={handleNext} variant="contained">
                  Next
                </Button>
              ) : (
                <Button type="submit" variant="contained" startIcon={<Send />}>
                  Submit
                </Button>
              )}
            </Box>
          </form>
        </Paper>
      </Container>

      <Alert
        open={alert.open}
        onClose={handleCloseAlert}
        severity={alert.severity}
        message={alert.message}
      />
    </Box>
  );
};

export default LoanApplicationForm;
